import React, { useState } from 'react';
import { Card, Badge, Button, Avatar, Icon } from '../../../components/ui';

export interface ShippingRate {
  provider: string;
  service_id: string;
  service_name: string;
  price: number;
  currency: string;
  eta_days: number | null;
  logo_url?: string;
}

interface RateCardListProps {
  rates: ShippingRate[];
  /** Draft order id, if one has already been created for this quote. Empty when no draft exists yet. */
  orderId: string;
  onSelect: (payload: string, rate?: ShippingRate) => void;
  /** True while a chat turn is already in flight — disables all Select buttons. */
  disabled?: boolean;
}

const RateCardList: React.FC<RateCardListProps> = ({ rates, orderId, onSelect, disabled }) => {
  const [clickedServiceId, setClickedServiceId] = useState<string | null>(null);

  if (!rates || rates.length === 0) return null;

  const handleSelect = (rate: ShippingRate) => {
    if (disabled || clickedServiceId) return;
    setClickedServiceId(rate.service_id);
    if (orderId) {
      // A draft order already exists for this quote — send the structured selection directly.
      onSelect(
        JSON.stringify({
          type: 'rate_selection',
          order_id: orderId,
          service_id: rate.service_id,
          accepted: true,
        }),
        rate
      );
    } else {
      // No draft yet — ask the agent in plain language to create one for this service.
      onSelect(
        `I choose ${rate.service_name} (service id: ${rate.service_id}, ${rate.price.toFixed(2)} ${rate.currency}). Please create the draft order.`,
        rate
      );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '8px 0' }}>
      <style>{`
        @keyframes rateCardSpin { to { transform: rotate(360deg); } }
        .rate-card-spin { display: inline-flex; animation: rateCardSpin 0.7s linear infinite; }
      `}</style>
      {rates.map((rate) => {
        const isClicked = clickedServiceId === rate.service_id;
        const isDisabled = disabled || clickedServiceId !== null;
        return (
          <Card
            key={rate.service_id}
            pad
            hover
            style={{ transition: 'box-shadow 0.2s' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {rate.logo_url ? (
                <img
                  src={rate.logo_url}
                  alt={rate.provider}
                  style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }}
                />
              ) : (
                <Avatar style={{ width: 44, height: 44, flexShrink: 0, background: 'var(--bg-elev)' }}>
                  <Icon name="truck" size={20} style={{ color: 'var(--brand-blue)' }} />
                </Avatar>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {rate.provider}
                </div>
                <div className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {rate.service_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--brand-blue)' }}>
                    {rate.price.toFixed(2)} {rate.currency}
                  </span>
                  {rate.eta_days != null && (
                    <Badge tone="info" style={{ fontSize: 11 }}>
                      ETA: {rate.eta_days} day{rate.eta_days !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>

              <Button
                variant="primary"
                size="sm"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                disabled={isDisabled}
                onClick={() => handleSelect(rate)}
              >
                {isClicked ? (
                  <span className="rate-card-spin"><Icon name="refresh" size={13} /></span>
                ) : (
                  'Select →'
                )}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default RateCardList;
